/**
 * Item Timer hook.
 *
 * Renders a cook item duration timer. Allows us to update the counter every second without requiring a server roundtrip.
 */
export const ItemTimer = {
  mounted() {
    this.setTimer();
  },

  updated() {
    this.setTimer();
  },

  setTimer() {
    this.el.innerText = formatDuration(this.el.dataset.startedAt);

    if (this.el.timerRef) {
      return;
    }
    this.el.timerRef = window.setInterval(() => {
      this.el.innerText = formatDuration(this.el.dataset.startedAt);
    }, 1000);
  },

  destroyed() {
    if (this.el.timerRef) {
      window.clearInterval(this.el.timerRef);
    }
  },
};

/**
 * Generic timer that will count up or down depending on if the target timestamp is in the future
 * or in the past. Can be configured to only count minutes or to use "T+" which will count appear as
 * "+05:23" once the timestamp that was in the future enters into the past.
 * 
 * 
 * ### Example
 * 
     <div
        phx-hook="Timer"
        data-timestamp={Timex.shift(Timex.now, minutes: 5)} # Timestamp to count from or to
        data-countdown="true" # To use T+ once future timestamp enters past
        data-minutes="false" # To only display in minutes. eg "72" instead of "01:12:00"
      />
 */
export const Timer = {
  mounted() {
    this.setTimer();
  },

  updated() {
    this.setTimer();
  },

  setTimer() {
    this.el.innerText = this.calculate_display();

    this.setClass();

    if (this.el.timerRef) {
      return;
    }
    this.el.timerRef = window.setInterval(() => {
      this.el.innerText = this.calculate_display();
      this.setClass();
    }, 1000);
  },

  setClass() {
    if (this.el.dataset.countdown != null && timestamp < new Date()) {
      this.el.classList.add("over-time");
    }
  },

  calculate_display() {
    now = Date();

    timestamp = Date.parse(this.el.dataset.timestamp);

    if (!timestamp || isNaN(timestamp)) {
      return "";
    }

    minutes_only = this.el.dataset.minutes != null;
    in_past = timestamp < new Date();
    result = this.formatMs(new Date() - timestamp, minutes_only);

    if (this.el.dataset.countdown != null && timestamp < new Date()) {
      return `+${result}`;
    } else {
      return result;
    }
  },

  formatMs(ms, in_minutes) {
    if (ms < 0) ms = -ms;

    if (in_minutes) {
      return "" + Math.floor(ms / 60000);
    } else {
      hour = Math.floor(ms / 3600000);
      minute = Math.floor(ms / 60000) % 60;
      second = Math.floor(ms / 1000) % 60;

      seconds = `${second}`.padStart(2, 0);
      minutes = `${minute}`.padStart(2, 0);

      if (hour > 0) {
        return `${hour}:${minutes}:${seconds}`;
      } else {
        return `${minutes}:${seconds}`;
      }
    }
  },

  destroyed() {
    if (this.el.timerRef) {
      window.clearInterval(this.el.timerRef);
    }
  },
};

/**
 * Countdown hook that counts down towards a given future timestamp.
 *
 * By default this updates every second as h:mm:ss / mm:ss. Set
 * `data-minutes="true"` to count in minutes (and hours) and update about
 * once a minute instead — used for group-order deadlines so the clock is
 * not stressful. `Format.timer`'s `data-minutes` is a different feature
 * (offset a clock) on the Timer hook; do not overload it.
 *
 * ### Example
 *
    <div
      phx-hook="Countdown"
      data-timestamp={Timex.shift(Timex.now, minutes: 5)}
      data-minutes="true"
    />
 */

export const Countdown = {
  mounted() {
    this.endTimestamp = Date.parse(this.el.dataset.timestamp);
    this.setTimer();
  },

  updated() {
    this.setTimer();
  },

  minutesOnly() {
    return this.el.dataset.minutes === "true";
  },

  setTimer() {
    if (this.el.timerRef) {
      window.clearInterval(this.el.timerRef);
    }

    this.updateDisplay();

    const interval = this.minutesOnly() ? 60_000 : 1_000;

    this.el.timerRef = window.setInterval(() => {
      this.updateDisplay();
    }, interval);
  },

  updateDisplay() {
    const now = new Date();

    if (
      !this.endTimestamp ||
      isNaN(this.endTimestamp) ||
      this.endTimestamp <= now
    ) {
      this.el.innerText = this.minutesOnly() ? "0 min" : "00:00";
      window.clearInterval(this.el.timerRef);
      return;
    }

    const remainingMs = this.endTimestamp - now;
    this.el.innerText = this.formatMs(remainingMs);
  },

  formatMs(ms) {
    if (this.minutesOnly()) {
      const totalMinutes = Math.max(1, Math.ceil(ms / 60000));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (hours > 0) {
        return minutes > 0 ? `${hours} hr ${minutes} min` : `${hours} hr`;
      }

      return `${totalMinutes} min`;
    }

    const seconds = `${Math.floor(ms / 1000) % 60}`.padStart(2, 0);
    const minutes = Math.floor(ms / 60000) % 60;
    const hours = Math.floor(ms / 3600000);

    if (hours > 0) {
      return `${hours}:${minutes}:${seconds}`;
    } else {
      return `${minutes}:${seconds}`;
    }
  },

  destroyed() {
    if (this.el.timerRef) {
      window.clearInterval(this.el.timerRef);
    }
  },
};

/**
 * Clock hook.
 * 
 * Dynamically displays either the current time or a time offset by a specified number of minutes into the future.
 * Updates every minute without needing a server roundtrip.
 * 
 * - If "data-minutes" is not provided, it displays the current time based on the given locale and timezone.
 * - If "data-minutes" attribute is provided on the element, it will display the time offset by that many minutes.
 *   Note that we round the time up to the top of next minute.
 * 
 * ### Example 1: Displaying current time
 * 
    <div 
        phx-hook="Clock" 
        data-locale="en-US"   # Desired locale format
        data-timezone="UTC"  # Desired timezone
    />
 *  
 * ### Example 2: Displaying time 10 minutes from now
 * 
  <div 
     phx-hook="Clock" 
     data-locale="en-US"     # Desired locale format
     data-timezone="UTC"    # Desired timezone
     data-minutes="10"      # Set the time 10 minutes into the future
  />
 */
export const Clock = {
  mounted() {
    this.setTimer();
  },

  updated() {
    this.setTimer();
  },

  setTimer() {
    if (this.el.timerRef) {
      return;
    }

    const locale = this.el.dataset.locale;
    const timezone = this.el.dataset.timezone;
    const minutesFromNow = parseInt(this.el.dataset.minutes, 10);

    if (isNaN(minutesFromNow)) {
      this.el.innerText = formatNow(locale, timezone);
    } else {
      this.el.innerText = formatTimeInFuture(minutesFromNow, locale, timezone);
    }

    const nextMinute = 60 - new Date().getSeconds();
    if (nextMinute > 0) {
      window.setTimeout(() => {
        this.setTimer();
      }, nextMinute * 1_000);
    } else {
      this.el.timerRef = window.setInterval(() => {
        if (isNaN(minutesFromNow)) {
          this.el.innerText = formatNow(locale, timezone);
        } else {
          this.el.innerText = formatTimeInFuture(
            minutesFromNow,
            locale,
            timezone,
          );
        }
      }, 60_000);
    }
  },

  destroyed() {
    if (this.el.timerRef) {
      window.clearInterval(this.el.timerRef);
    }
  },
};

export const formatDuration = (since) => {
  if (!since) {
    return "";
  }

  since = Date.parse(since);
  if (!since || isNaN(since)) {
    return "";
  }

  const durationSeconds = Math.floor((new Date() - since) / 1_000);

  const seconds = durationSeconds % 60;
  let result = ("" + seconds).padStart(2, 0);

  const minutes = ("" + (Math.floor(durationSeconds / 60) % 60)).padStart(2, 0);
  result = `${minutes}:${result}`;

  if (durationSeconds > 3600) {
    const hours = ("" + Math.floor(durationSeconds / 3600)).padStart(2, 0);
    result = `${hours}:${result}`;
  }

  return result;
};

export const formatNow = (locale, timezone) =>
  new Date().toLocaleString(locale, {
    timeZone: timezone,
    dateStyle: "long",
    timeStyle: "short",
  });

export const formatTimeInFuture = (minutesFromNow, locale, timezone) => {
  let futureTime = new Date();
  futureTime.setMinutes(futureTime.getMinutes() + minutesFromNow);
  // Set seconds to 0 to round to the next minute
  futureTime.setSeconds(0);
  futureTime.setMinutes(futureTime.getMinutes() + 1);
  return futureTime.toLocaleString(locale, {
    timeZone: timezone,
    timeStyle: "short",
  });
};
